import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductPrice, { ProductPriceStyles } from "@MasterBluePrint/Overrides/Components/ProductPrice";

import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { StyledIcon } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductQuantityBreakPricingTable from "@MasterBluePrint/Components/ProductQuantityBreakPricingTable";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@MasterBluePrint/Overrides/Components/ProductQuantityBreakPricing";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    showPrice: boolean;
    showAddToList: boolean;
    displayQtyBreakPricingInTable: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    minMultiGridItem?: InjectableCss;
    minMultiWrapper?: InjectableCss;
    minQtyLabel?: InjectableCss;
    minQtyValue?: InjectableCss;
    nonCancellableNonReturnableContainer?: InjectableCss;
    nonCancellableNonReturnableText?: InjectableCss;
    nonCancellableNonReturnableAnchor?: InjectableCss;
    imageBadgeGridItem?: InjectableCss;
    textBadgeGridItem?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 1.25rem;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: .5rem 0;        
                @media screen and (max-width: 1280px) {
                    position:initial;
                    text-align:left;
                    width:fit-content;
                }
            `,
        },
        priceWrapper: {
            css: css`
                @media screen and (max-width: 1280px) {
                    top:unset !important;
                    right:unset !important;
                    bottom:33px;
                    &:after {
                        right:unset;
                        left:2.813rem;
                    }
                }
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: block;
            align-items: flex-end;
            margin-left: .625rem;
            @media screen and (max-width: 1280px) {
                margin-top: .125rem;
                margin-left: 0;
                .qty-container {
                    margin-top:0 !important;
                    margin-bottom: .625rem !important;
                    input {
                        min-width: 9.8rem !important;
                    }
                }
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
            @media screen and (max-width: 1280px) {
                max-width: 19.125rem;
            }
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-top: .5rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: .31250625rem;
            min-height: 2rem;
        `,
    },
    minMultiGridItem: {
        css: css`
            margin-top:3px;
        `,
    },
    minMultiWrapper: {
        css: css`            
            font-size: .9rem !important;
            word-break: normal;
            margin-top: 0;
            margin-bottom: .1rem;
            position: relative;
            display: block;
            color: #333F48;
            font-family: 'Lato', sans-serif;
            white-space:nowrap;
            font-style:italic;
        `,
    },
    minQtyLabel: {
        css: css`
            margin-right:3px;
            font-weight: 600;
        `,
    },
    minQtyValue: {
        css: css`
            font-weight:400;
        `,
    },
    nonCancellableNonReturnableContainer: {
        css: css`
            font-weight: 600;
            font-size: .85rem;
            margin-bottom: .2em;
            text-align: left;
            color: #333F48;
            @media screen and (min-width: 1400px) {
                max-width: 10.375rem;
                max-width: 10.625rem;
                white-space: normal;
                margin-bottom: .625rem;
            }
            @media screen and (max-width: 1280px) {
                padding: .9375rem .9375rem 0 0;
                position: relative;
                margin-top: 0.3125rem;
                margin-bottom: -0.7rem;
            }
        `,
    },
    nonCancellableNonReturnableText: {
        css: css`
        color: #333F48;
        font-family: 'Roboto', sans-serif;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: .003rem
        line-height: 1.1875rem;
        text-align: center;
        text-align: left;
        font-weight: normal;
        `,
    },
    nonCancellableNonReturnableAnchor: {
        css: css`
        color: #333F48;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: .003rem
        line-height: 1.1875rem;
        text-align: center;
        text-align: left;
        font-family: 'Roboto', sans-serif;
        font-weight: normal;
        &:hover {
            text-decoration: underline !important;
            color: #333F48 !important;
        }
        span {
            font-size: .875rem;
            color: #333F48 !important;

            &:hover {
                text-decoration: underline !important;
                color: #333F48 !important;
            }
        }

        `,
    },
    imageBadgeGridItem: {
        css: css`
            &.plp-badge {
                div[class*='FlexItem'],
                div[class^='FlexItem'] {
                    width: 100%;
                    max-width: 100%;
                    flex-basis: 100%;
                }
            }
        `,
    },
    textBadgeGridItem: {
        css: css`
            // @media screen and (min-width: 1201px) {
            //     display: none;
            // }
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    product,
    productContext,
    productSettings,
    showPrice,
    showAddToList, 
    displayQtyBreakPricingInTable,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges }) => {

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                        hideVat={true}
                    />

                    {displayQtyBreakPricingInTable ? <ProductQuantityBreakPricingTable extendedStylesTable={styles.quantityBreakPricing} /> : <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />}
                </>
            )}
            {showTextBadges && textBadges && (
                <StyledWrapper {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </StyledWrapper>
            )}
            {showImageBadges && imageBadges && (
                <StyledWrapper {...styles.imageBadgeGridItem} className="plp-badge">
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.minMultiGridItem}>
                {product.minimumOrderQty > 0 ? (
                    <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                        <Typography {...styles.minQtyLabel}>
                            {translate("Min order qty: ")}
                        </Typography>
                        <Typography {...styles.minQtyValue}>
                            {FormattedCommaSeparatedValue(product.minimumOrderQty)}
                        </Typography>
                    </StyledWrapper>
                ) : ''}
                {product.detail?.multipleSaleQty && product.detail?.multipleSaleQty > 1 ? (
                    <StyledWrapper {...styles.minMultiWrapper} className="min-multi-detail">
                        <Typography {...styles.minQtyLabel}>
                            {translate("Multiples of: ")}
                        </Typography>
                        <Typography {...styles.minQtyValue}>
                            {FormattedCommaSeparatedValue(product.detail?.multipleSaleQty)}
                        </Typography>
                    </StyledWrapper>
                ) : ''}
            </StyledWrapper>
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>
            {product.properties?.ncnr?.toLowerCase() === "true" ? (
                <StyledWrapper {...styles.nonCancellableNonReturnableContainer}>
                    <Typography {...styles.nonCancellableNonReturnableText}>
                        <Link {...styles.nonCancellableNonReturnableAnchor} href={String(siteMessage("NonCancellableNonReturnableLink"))} title={String(siteMessage("NonCancellableNonReturnableText"))}>
                            {siteMessage("NonCancellableNonReturnableText")}
                        </Link>
                    </Typography>
                </StyledWrapper>
            ) : ("")}

        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));